import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Card,
  Table,
  Tag,
  Badge,
  Spin,
  Empty,
  Row,
  Col,
  Space,
  Button,
  Tooltip,
  Typography,
} from "antd";
import {
  ShopOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  BarcodeOutlined,
} from "@ant-design/icons";
import { getLocationProducts } from "../../../actions/ecommerce/onlineOrderAction";

const { Text } = Typography;

const LocationEcommerceProducts = ({
  location,
  reload,
  outletlist = [],
  getLocationProducts,
  Auth,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Excluded locations: "Main Warehouse" and "Ecommerce"
  const EXCLUDED_LOCATIONS = ["Main Warehouse", "Ecommerce"];

  const getCurrentLocationInfo = () => {
    const locValue = location && location.current !== undefined ? location.current : "";
    if (!locValue) {
      return null;
    }
    const matchedOutlet = outletlist.find(
      (o) =>
        String(o.id) === String(locValue) ||
        String(o.name).trim().toLowerCase() === String(locValue).trim().toLowerCase()
    );
    if (matchedOutlet) {
      return matchedOutlet;
    }
    if (Auth && Auth.profile && Auth.profile.branch) {
      const branch = Auth.profile.branch;
      if (
        String(branch.id) === String(locValue) ||
        String(branch.name).trim().toLowerCase() === String(locValue).trim().toLowerCase()
      ) {
        return branch;
      }
    }
    return { id: locValue, name: String(locValue) };
  };

  const currentOutlet = getCurrentLocationInfo();
  const currentLocName = currentOutlet ? currentOutlet.name : "";
  const isExcludedLocation =
    currentLocName &&
    EXCLUDED_LOCATIONS.some(
      (excluded) => excluded.toLowerCase() === currentLocName.trim().toLowerCase()
    );

  const fetchData = async () => {
    if (isExcludedLocation) {
      setData([]);
      return;
    }
    setLoading(true);
    try {
      const params = {
        status: "Ecommerce Pending",
      };
      if (location && location.current) {
        params.location_id = location.current;
      }

      const res = await getLocationProducts(params);
      if (res && res.success) {
        setData(res.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching location products:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isExcludedLocation) {
      setData([]);
    } else {
      fetchData();
    }
  }, [location && location.current, outletlist, reload]);



  const productColumns = [
    {
      title: "SL",
      key: "sl",
      width: 50,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      render: (text, record) => (
        <div>
          <Text strong style={{ color: "#1890ff" }}>
            {text}
          </Text>
          {record.product_code && (
            <div style={{ fontSize: "11px", color: "#8c8c8c" }}>
              Code: {record.product_code}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      key: "barcode",
      width: 140,
      render: (barcode) =>
        barcode ? (
          <Space size={4}>
            {/* <BarcodeOutlined style={{ color: "#595959" }} /> */}
            <Text code>{barcode}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Attribute / Size",
      dataIndex: "attribute",
      key: "attribute",
      width: 120,
      render: (attr) =>
        attr ? (
          <Tag color="purple" style={{ fontWeight: 500 }}>
            {attr}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Order Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "center",
      render: (qty) => (
        <Badge
          count={qty}
          showZero
          style={{
            backgroundColor: "#52c41a",
            fontSize: "13px",
            fontWeight: "bold",
            padding: "0 8px",
          }}
        />
      ),
    },
    {
      title: "Related Orders",
      dataIndex: "orders",
      key: "orders",
      render: (orders) => {
        if (!orders || orders.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Space wrap size={[4, 4]}>
            {orders.map((o, idx) => (
              <Tooltip
                key={idx}
                title={`Status: ${o.order_status || "N/A"} | Qty: ${o.order_quantity || 1}`}
              >
                <Tag color="blue" style={{ fontSize: "11px", cursor: "pointer" }}>
                  {o.invoice_number}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
  ];

  if (isExcludedLocation) {
    return null;
  }

  return (
    <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
      <Card

        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <Space align="center">
              <ShopOutlined style={{ fontSize: "18px", color: "#1890ff" }} />
              <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                Incomming Ecommerce 
              </span>
              <Badge
                count={data.reduce((acc, curr) => acc + (curr.total_quantity || 0), 0)}
                overflowCount={9999}
                style={{ backgroundColor: "#1890ff" }}
              />
            </Space>

            <Button
              type="text"
              icon={<ReloadOutlined spin={loading} />}
              onClick={fetchData}
              size="small"
            >
              Refresh
            </Button>
          </div>

        }
        style={{
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          borderRadius: "8px",
        }}
        bodyStyle={{ padding: "16px" }}
      >
        <Spin spinning={loading}>
          {(!data || data.length === 0) && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No ecommerce ordered items found for selected location"
            />
          ) : (
            <Row gutter={[16, 16]}>
              {data.map((loc) => (
                <Col span={24} key={loc.location_id || loc.location_name}>
                  <Card
                    type="inner"
                    style={{
                      border: "1px solid #e8e8e8",
                      borderRadius: "6px",
                      background: "#fafafa",
                    }}
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                        }}
                      >
                        <Space>
                          <ShoppingOutlined style={{ color: "#fa8c16" }} />
                          <span style={{ fontWeight: 600, fontSize: "15px" }}>
                            {loc.location_name}
                          </span>
                          {loc.warehouse_address && (
                            <Text
                              type="secondary"
                              style={{ fontSize: "12px", marginLeft: 8 }}
                            >
                              ({loc.warehouse_address.replace(/<[^>]+>/g, "")})
                            </Text>
                          )}
                        </Space>
                        <Space size="middle">
                          <Tag color="cyan">
                            Distinct Orders: <strong>{loc.total_orders_count}</strong>
                          </Tag>
                          <Tag color="green">
                            Total Item Qty: <strong>{loc.total_quantity}</strong>
                          </Tag>
                        </Space>
                      </div>
                    }
                  >
                    <Table
                      dataSource={loc.products || []}
                      columns={productColumns}
                      rowKey={(record, idx) =>
                        `${record.barcode}_${record.product_name}_${record.attribute}_${idx}`
                      }
                      pagination={
                        loc.products && loc.products.length > 5
                          ? { pageSize: 5, size: "small" }
                          : false
                      }
                      size="small"
                      bordered
                      style={{ background: "#fff" }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Spin>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  Auth: state.auth,
});

export default connect(mapStateToProps, {
  getLocationProducts,
})(LocationEcommerceProducts);
